import { formatBarBeat, formatSeconds, secondsToBeats, tempoOf, timeSignatureOf } from '../../lib/sound-doc.js';
import { soundPlayer, usePlayback } from '../../lib/sound-player.js';
import { summarizeAnalysis } from '../../lib/sound-render.js';
import { useSound } from '../../state/sound.js';
import { IconButton } from '../ui/controls.js';

/**
 * Bottom strip: transport (play/stop, loop, position, tempo), the live master
 * meter and the last render's analysis (peak, true peak, LUFS, RMS, centroid).
 * The analysis stays from the last good render while a new one runs.
 */

function db(v: number | null, unit = 'dB'): string {
  if (v === null) return '—';
  if (!Number.isFinite(v)) return `-∞ ${unit}`;
  return `${v.toFixed(1)} ${unit}`;
}

function meterPct(dbValue: number): number {
  if (!Number.isFinite(dbValue)) return 0;
  return Math.max(0, Math.min(100, ((dbValue + 60) / 60) * 100));
}

function Meter() {
  const left = usePlayback((s) => s.meterDb);
  const right = usePlayback((s) => s.meterRightDb);
  const clip = left > -0.1 || right > -0.1;
  return (
    <div className="snd-meter" role="meter" aria-label="Output level" aria-valuemin={-60} aria-valuemax={0} aria-valuenow={Number.isFinite(left) ? Math.round(left) : -60} title={`L ${db(left)} · R ${db(right)}`}>
      <span className="snd-meter-bar">
        <span style={{ width: `${meterPct(left)}%` }} />
      </span>
      <span className="snd-meter-bar">
        <span style={{ width: `${meterPct(right)}%` }} />
      </span>
      <span className={`snd-meter-clip${clip ? ' is-on' : ''}`} aria-hidden="true" />
    </div>
  );
}

function Position() {
  const position = usePlayback((s) => s.position);
  const duration = usePlayback((s) => s.duration);
  const doc = useSound((s) => s.doc);
  const song = !!doc && (doc.kind === 'song' || !!doc.tracks?.length);
  return (
    <span className="snd-position mono" aria-live="off" title="Playhead">
      {song && doc && <strong>{formatBarBeat(secondsToBeats(position, tempoOf(doc)), timeSignatureOf(doc))}</strong>}
      <span>
        {formatSeconds(position)} <span className="muted">/ {formatSeconds(duration)}</span>
      </span>
    </span>
  );
}

export function Transport() {
  const playing = usePlayback((s) => s.playing);
  const loop = usePlayback((s) => s.loop);
  const loopOn = usePlayback((s) => s.loopOn);
  const monitorDb = usePlayback((s) => s.monitorDb);
  const doc = useSound((s) => s.doc);
  const audio = useSound((s) => s.audio);
  const analysis = useSound((s) => s.analysis);
  const render = useSound((s) => s.render);
  const s = useSound.getState();
  const summary = summarizeAnalysis(analysis, audio);
  const song = !!doc && (doc.kind === 'song' || !!doc.tracks?.length);
  const loudnessTarget = doc?.master?.loudness;

  return (
    <div className="snd-transport" role="toolbar" aria-label="Transport">
      <div className="tb-group segmented">
        <IconButton icon="restart" title="To start" shortcut="Home" onClick={() => soundPlayer.seek(loop && loopOn ? loop.start : 0)} disabled={!audio} />
        <IconButton icon={playing ? 'stop' : 'play'} title={playing ? 'Stop' : 'Play'} shortcut="Space" active={playing} onClick={() => soundPlayer.toggle()} disabled={!audio} />
        <IconButton
          icon="refresh"
          title={loop ? `Loop ${loop.start.toFixed(2)}s–${loop.end.toFixed(2)}s` : 'Loop (Shift+drag on the ruler to set a region; loops the whole sound otherwise)'}
          shortcut="L"
          active={loopOn}
          onClick={() => {
            if (!loop && audio) soundPlayer.setLoop({ start: 0, end: audio.left.length / audio.rate });
            else soundPlayer.setLoopOn(!loopOn);
          }}
          disabled={!audio}
        />
      </div>
      <Position />
      {song && doc && (
        <>
          <label className="snd-inline" title="Tempo (quarter-note beats per minute)">
            <span className="muted">BPM</span>
            <input
              type="number"
              min={20}
              max={400}
              step={1}
              value={tempoOf(doc)}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (v >= 20 && v <= 400) s.setPath(['tempo'], v);
              }}
              aria-label="Tempo in BPM"
            />
          </label>
          <label className="snd-inline" title="Time signature">
            <input
              className="snd-ts"
              value={timeSignatureOf(doc).join('/')}
              onChange={(e) => {
                const m = e.target.value.match(/^(\d+)\s*\/\s*(\d+)$/);
                if (m && Number(m[1]) > 0 && [1, 2, 4, 8, 16].includes(Number(m[2]))) s.setPath(['timeSignature'], [Number(m[1]), Number(m[2])]);
              }}
              aria-label="Time signature"
            />
          </label>
        </>
      )}
      <span className="bar-sep" />
      <Meter />
      <div className="snd-tr-stats" aria-label="Analysis of the last render">
        <span title="Sample peak">
          <span className="muted">Peak</span> {db(summary.peakDb)}
        </span>
        <span title="True peak (dBTP)">
          <span className="muted">TP</span> {db(summary.truePeakDb, 'dBTP')}
        </span>
        <span title={loudnessTarget !== undefined ? `Integrated loudness (target ${loudnessTarget} LUFS)` : 'Integrated loudness (ITU-R BS.1770)'}>
          <span className="muted">LUFS</span> {summary.lufs === null ? '—' : Number.isFinite(summary.lufs) ? summary.lufs.toFixed(1) : '-∞'}
        </span>
        <span title="RMS level">
          <span className="muted">RMS</span> {db(summary.rmsDb)}
        </span>
        {summary.centroid !== null && (
          <span title="Spectral centroid (brightness)">
            <span className="muted">Centroid</span> {Math.round(summary.centroid)} Hz
          </span>
        )}
        {summary.duration !== null && (
          <span title="Rendered length">
            <span className="muted">Len</span> {summary.duration.toFixed(2)}s
          </span>
        )}
      </div>
      <span className="bar-spacer" />
      {render.warnings.length > 0 && (
        <span className="snd-warn" title={render.warnings.join('\n')}>
          {render.warnings.length} warning{render.warnings.length === 1 ? '' : 's'}
        </span>
      )}
      <label className="snd-inline" title="Monitor volume (not saved in the sound)">
        <span className="muted">Vol</span>
        <input type="range" min={-40} max={6} step={1} value={monitorDb} onChange={(e) => soundPlayer.setMonitor(Number(e.target.value))} aria-label="Monitor volume" />
      </label>
    </div>
  );
}
